import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight } from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { CreatorSummaryTileButton } from './CreatorSummaryTile';
import './CommunityTopCreatorsRow.css';

const TOP_CREATORS_MIN_SLOTS = 20;
/** Minimum gap used when deciding how many tiles fit; leftover is absorbed by space-between. */
const MIN_GAP_PX = 16; // 1rem
const FIT_EPS_PX = 2;
/** Rows of tiles to display; mobile gets a second row since only ~3 tiles fit per row. */
const DESKTOP_ROWS = 1;
const MOBILE_ROWS = 2;
const MOBILE_MQ = '(max-width: 62.4375rem)';

const TXT_USER_ONE = {
    fr: 'utilisateur inscrit',
    en: 'registered user',
    de: 'registrierter Nutzer',
    es: 'usuario registrado',
    pt: 'usuário registrado',
};
const TXT_USER_MANY = {
    fr: 'utilisateurs inscrits',
    en: 'registered users',
    de: 'registrierte Nutzer',
    es: 'usuarios registrados',
    pt: 'usuários registrados',
};

function countLabel(count, lang) {
    const dict = count === 1 ? TXT_USER_ONE : TXT_USER_MANY;
    return `${count} ${dict[lang] || dict.en}`;
}

/** "45 utilisateurs inscrits" — shared by the top-creators strip and the creators directory. */
export function RegisteredUsersCount({ total = 0, lang = 'fr' }) {
    return <span className="community-top-creators-count">{countLabel(Number(total) || 0, lang)}</span>;
}

function measureTileWidth(rowEl) {
    const tile = rowEl?.querySelector('.community-top-creator-tile');
    return tile?.getBoundingClientRect().width ?? 92;
}

/** How many equal-width tiles with at least `minGap` between them fit in `w`. */
function countTilesThatFit(w, tileW, minGapPx, maxCreators) {
    if (!maxCreators || tileW <= 0 || w <= 0) return 0;
    const step = tileW + minGapPx;
    if (step <= 0) return 0;
    const n = Math.floor((w + minGapPx + FIT_EPS_PX) / step);
    return Math.max(0, Math.min(maxCreators, TOP_CREATORS_MIN_SLOTS, n));
}

function isMobileViewport() {
    return typeof window !== 'undefined' && window.matchMedia(MOBILE_MQ).matches;
}

/**
 * Fit-to-width top creators strip (same as Community page).
 */
export function CommunityTopCreatorsRow({
    creators = [],
    totalCreators = null,
    lang = 'fr',
    title = null,
    titleHref = null,
    onSelect = null,
    titleAs = 'h2',
    mutedTitle = false,
}) {
    const navigate = useNavigate();
    const [topFitCount, setTopFitCount] = useState(12);
    const [rowCount, setRowCount] = useState(DESKTOP_ROWS);
    const measureRef = useRef(null);
    const TitleTag = titleAs === 'h3' ? 'h3' : 'h2';

    const titleTo = titleHref || (lang ? `/${lang}/community/creators` : '/fr/community/creators');

    const padded = useMemo(() => {
        const r = [...creators];
        while (r.length < TOP_CREATORS_MIN_SLOTS) r.push(null);
        return r;
    }, [creators]);

    const measureFit = useCallback(() => {
        const el = measureRef.current;
        if (!el) return;
        setRowCount(isMobileViewport() ? MOBILE_ROWS : DESKTOP_ROWS);
        if (padded.length === 0) {
            setTopFitCount(0);
            return;
        }
        const w = el.clientWidth;
        const row = el.querySelector('.community-top-creators-row');
        if (!row || w <= 0) {
            setTopFitCount(Math.min(1, padded.length));
            return;
        }
        const tileW = measureTileWidth(row);
        const cap = Math.min(padded.length, TOP_CREATORS_MIN_SLOTS);
        const next = countTilesThatFit(w, tileW, MIN_GAP_PX, cap);
        setTopFitCount((prev) => (prev === next ? prev : next));
    }, [padded]);

    useLayoutEffect(() => {
        measureFit();
        const el = measureRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(() => measureFit());
        ro.observe(el);
        const mq = window.matchMedia(MOBILE_MQ);
        const onMq = () => measureFit();
        if (mq.addEventListener) mq.addEventListener('change', onMq);
        else mq.addListener(onMq);
        return () => {
            ro.disconnect();
            if (mq.removeEventListener) mq.removeEventListener('change', onMq);
            else mq.removeListener(onMq);
        };
    }, [measureFit, padded, topFitCount]);

    const slotCount = Math.min(topFitCount * rowCount, TOP_CREATORS_MIN_SLOTS);
    const total = Number(totalCreators) || 0;
    // Omitting the prop hides the count entirely; passing 0 still shows "0 …".
    const showCount = totalCreators !== null && totalCreators !== undefined;

    const handleSelect = (c) => {
        if (onSelect) {
            onSelect(c);
            return;
        }
        if (!c?.public_slug) return;
        navigate(`/${lang}/community/${encodeURIComponent(c.public_slug)}`);
    };

    return (
        <section className="community-top-creators" aria-label={title || undefined}>
            {title ? (
                <div
                    className={
                        mutedTitle
                            ? 'community-top-creators-head community-top-creators-head--muted'
                            : 'community-top-creators-head'
                    }
                >
                    <Link
                        className={
                            mutedTitle
                                ? 'community-top-creators-title-wrap community-top-creators-title-wrap--muted'
                                : 'community-top-creators-title-wrap'
                        }
                        to={titleTo}
                    >
                        <TitleTag
                            className={
                                mutedTitle
                                    ? 'community-top-creators-title community-top-creators-title--muted'
                                    : 'community-top-creators-title'
                            }
                        >
                            <span>{title}</span>
                            <ChevronRight className="community-top-creators-chevron" fontSize="inherit" aria-hidden />
                        </TitleTag>
                    </Link>
                    {showCount ? (
                        <RegisteredUsersCount total={total} lang={lang} />
                    ) : null}
                </div>
            ) : null}
            <div ref={measureRef} className="community-top-creators-measure-wrap">
                <div className="community-top-creators-row">
                    {padded.slice(0, slotCount).map((c, idx) => (
                        <CreatorSummaryTileButton
                            key={c ? `row-${c.public_slug}` : `row-ph-${idx}`}
                            creator={c}
                            lang={lang}
                            onSelect={handleSelect}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}
